import React from 'react';
import clsx from 'classnames';

import '@/style/components/Details.scss';

const DIRECTION = {
  VERTICAL: 'vertical',
  HORIZANTAL: 'horizantal',
};

interface DetailsMenuContextValue {
  minLabelSize?: number | string;
}
const DetailsMenuContext = React.createContext<DetailsMenuContextValue>({});
const useDetailsMenuContext = () => React.useContext(DetailsMenuContext);

interface DetailsMenuProps {
  children?: React.ReactNode;
  direction?: string;
  textAlign?: string;
  minLabelSize?: number | string;
  className?: string;
}

/**
 * Details menu.
 */
export function DetailsMenu({
  children,
  direction = DIRECTION.VERTICAL,
  textAlign,
  minLabelSize,
  className,
}: DetailsMenuProps) {
  return (
    <div
      className={clsx(
        'details-menu',
        {
          'details-menu--vertical': direction === DIRECTION.VERTICAL,
          'details-menu--horizantal': direction === DIRECTION.HORIZANTAL,
          [`align-${textAlign}`]: textAlign,
        },
        className,
      )}
    >
      <DetailsMenuContext.Provider value={{ minLabelSize }}>
        {children}
      </DetailsMenuContext.Provider>
    </div>
  );
}

/**
 * Detail item.
 */
interface DetailItemProps {
  label?: React.ReactNode;
  children?: React.ReactNode;
  name?: string;
  align?: string;
  multiline?: boolean;
  className?: string;
}

export function DetailItem({
  label,
  children,
  name,
  align,
  multiline,
  className,
}: DetailItemProps) {
  const { minLabelSize } = useDetailsMenuContext();

  return (
    <div
      className={clsx(
        'detail-item',
        {
          [`detail-item--${name}`]: name,
          [`align-${align}`]: align,
          [`detail-item--multilines`]: multiline,
        },
        className,
      )}
    >
      <div
        style={{
          minWidth: minLabelSize,
        }}
        className="detail-item__label"
      >
        {label}
      </div>
      <div className={clsx('detail-item__content')}>{children}</div>
    </div>
  );
}
